/**
 * `ActionDefinition.outputSchema` — declarative response→destination
 * binding contract (Key Technical Decision #4, flow-workflow-requests
 * plan). Lets an action definition declare, as data, which fields of a
 * handler's response should be written onto the contact / session, without
 * hand-written per-action mapping code.
 *
 * Shape: `outputSchema` is a JSON array of bindings. Each binding names a
 * `source` (a top-level response key or a dot-path into it), a
 * `destination` (where the resolved value goes), and an optional
 * `condition` gating whether the binding applies at all.
 *
 * On `markDelivered`, the service layer resolves bindings against the raw
 * handler response via `resolveOutputBindings` and persists the resolved
 * `{source, value, destination}` snapshot alongside `handlerResult` — so
 * later edits to `outputSchema` never rewrite history for past runs.
 *
 * Schema is the source of truth; TS types below are `z.infer` only (see
 * `terra/no-parallel-schema-and-type`).
 *
 * `outputSchema` is a Prisma JSON column — always read through
 * `parseOutputSchema` (which wraps `parseDbJson`), never `.parse()`
 * directly. A single drifted row must degrade to "no bindings", not 500.
 */
import { z } from 'zod';
import { parseDbJson, type DbJsonContext } from './db-json';

// ── Destination union ────────────────────────────────────────────────

/**
 * Where a resolved binding value is written.
 *
 * - `contact_field_overlay` — stage `value` onto the named contact field
 *   (an "awaiting sync" overlay until the real system-of-record catches
 *   up); `field` is the contact-state field key, not a raw SFDC API name.
 * - `session_effect` — apply a named side effect scoped to the requesting
 *   user's session (e.g. a toast, a local cache bust); `effect` is a
 *   registered effect name, resolved elsewhere.
 * - `display_only` — no write; the value is shown in the request detail
 *   view only.
 *
 * `sfdc_write` / `trigger_action` destinations are explicitly deferred
 * (see plan's "Deferred to Follow-Up Work").
 */
export const OutputDestinationSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('contact_field_overlay'), field: z.string().min(1) }),
  z.object({ type: z.literal('session_effect'), effect: z.string().min(1) }),
  z.object({ type: z.literal('display_only') }),
]);

export type OutputDestination = z.infer<typeof OutputDestinationSchema>;

// ── Binding (the outputSchema column shape) ─────────────────────────────

/**
 * One response→destination binding.
 *
 * `source` is a response key or dot-path (e.g. `"phone"` or
 * `"result.phone"`) resolved with plain dot-path lookup — no bracket/array
 * index syntax.
 *
 * `condition`, when present, is limited to the single form
 * `"<key or dot-path> == <literal>"` (literal: `true` | `false` | `null` |
 * a number | a quoted or bare string). Anything that doesn't parse as that
 * shape is treated as false (gate closed) — never thrown.
 */
export const OutputBindingSchema = z.object({
  source: z.string().min(1),
  destination: OutputDestinationSchema,
  condition: z.string().min(1).optional(),
});

export type OutputBinding = z.infer<typeof OutputBindingSchema>;

export const OutputBindingsSchema = z.array(OutputBindingSchema);

// ── Resolved snapshot (persisted at completion) ─────────────────────────

/**
 * A single resolved binding, persisted alongside `handlerResult` at
 * completion time. `destination` is carried through unchanged so the
 * snapshot is self-describing even if `outputSchema` is edited later.
 */
export const ResolvedOutputBindingSchema = z.object({
  source: z.string(),
  value: z.unknown(),
  destination: OutputDestinationSchema,
});

export type ResolvedOutputBinding = z.infer<typeof ResolvedOutputBindingSchema>;

export const ResolvedOutputSnapshotSchema = z.array(ResolvedOutputBindingSchema);

export type ResolvedOutputSnapshot = z.infer<typeof ResolvedOutputSnapshotSchema>;

// ── parseOutputSchema — trust-boundary read of the JSON column ──────────

/**
 * Parse `ActionDefinition.outputSchema` (a nullable Prisma JSON column)
 * into a binding array, never throwing. `null`/`undefined` (no bindings
 * configured) and a malformed column value both degrade to `[]` — an
 * action with a broken/absent `outputSchema` simply produces no bindings,
 * it doesn't fail the run.
 */
export function parseOutputSchema(value: unknown, ctx: DbJsonContext): OutputBinding[] {
  if (value === null || value === undefined) return [];
  return parseDbJson(OutputBindingsSchema, value, ctx) ?? [];
}

// ── resolveOutputBindings — pure resolver ───────────────────────────────

/**
 * Dot-path lookup (`"a.b.c"`) into a plain object/array tree. Returns
 * `undefined` for a missing key at any hop, a non-object intermediate
 * value, or a non-object `root` — never throws.
 */
function getByPath(root: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc === null || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[key];
  }, root);
}

/**
 * Parse the literal half of a `<key> == <literal>` condition:
 * `true` / `false` / `null`, a number, a single- or double-quoted string,
 * or (fallback) the bare string itself.
 */
function parseConditionLiteral(raw: string): unknown {
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  if (raw === 'null') return null;
  const quoted = /^'(.*)'$/.exec(raw) ?? /^"(.*)"$/.exec(raw);
  if (quoted) return quoted[1];
  if (raw !== '' && !Number.isNaN(Number(raw))) return Number(raw);
  return raw;
}

/**
 * Evaluate a `condition` string of the exact form `"<key> == <literal>"`
 * against `response`. Anything that doesn't match that shape — or whose
 * key is missing — evaluates to `false` (gate closed), never throws.
 */
function evaluateCondition(response: unknown, condition: string): boolean {
  const match = /^(.+?)\s*==\s*(.+)$/.exec(condition.trim());
  if (!match) return false;
  const [, rawKey, rawLiteral] = match;
  const actual = getByPath(response, rawKey.trim());
  const expected = parseConditionLiteral(rawLiteral.trim());
  return actual === expected;
}

/**
 * Resolve `bindings` against a handler `response`, dropping any binding
 * whose `condition` evaluates false and any binding whose `source` path
 * is missing from `response` — both are silent skips, not errors, since a
 * handler response legitimately varies run to run.
 */
export function resolveOutputBindings(
  response: unknown,
  bindings: OutputBinding[],
): ResolvedOutputBinding[] {
  const resolved: ResolvedOutputBinding[] = [];
  for (const binding of bindings) {
    if (binding.condition && !evaluateCondition(response, binding.condition)) continue;
    const value = getByPath(response, binding.source);
    if (value === undefined) continue;
    resolved.push({ source: binding.source, value, destination: binding.destination });
  }
  return resolved;
}
